/**
 * Bundle 102 (Phase 7) - Admin follow-ups review page.
 *
 * Route: /admin/followups/pending (super_admin only)
 *
 * DRAFT-FIRST safety: every follow-up email waits in the queue with
 * `delivery_status=draft`. Nothing leaves the system until an admin
 * approves. This page is that approval surface.
 *
 * Data sources:
 *   GET  /api/admin/followups/pending           (Bundle 102)
 *   POST /api/admin/followups/pending/approve
 *   POST /api/admin/followups/pending/dispatch
 *
 * No em/en dashes, no eslint-disable, no photo/image alt.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Send, Check, ShieldCheck } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const STATUS_BADGE = {
  draft: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  approved: 'bg-blue-50 text-blue-800 border-blue-200',
  dispatched: 'bg-success-50 text-success-700 border-success-200',
  cancelled: 'bg-neutral-100 text-neutral-500 border-neutral-200',
};

const AdminFollowupsPendingPage = () => {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('draft');
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [scheduler, setScheduler] = useState(null);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/followups/pending', {
        params: { status },
      });
      setRows(data?.rows || []);
    } catch (e) {
      toast.error('Could not load pending follow-ups.');
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduler = async () => {
    try {
      const { data } = await api.get(
        '/admin/followups/scheduler/status',
      );
      setScheduler(data);
    } catch (e) {
      // Non-blocking: page still works without scheduler tile.
    }
  };

  useEffect(() => { fetchRows(); }, [status]);
  useEffect(() => { fetchScheduler(); }, []);

  const selectedIds = useMemo(
    () => Object.entries(selected).filter(([, v]) => v).map(([k]) => k),
    [selected],
  );

  const toggleAll = () => {
    if (selectedIds.length === rows.length) setSelected({});
    else {
      const next = {};
      rows.forEach((r) => { next[r.id] = true; });
      setSelected(next);
    }
  };

  const approve = async () => {
    if (!selectedIds.length) { toast.error('Select at least one row.'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/admin/followups/pending/approve',
        { followup_ids: selectedIds });
      toast.success(`Approved ${data.approved} row(s).`);
      setSelected({});
      fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed.');
    } finally { setBusy(false); }
  };

  const dispatchNow = async () => {
    if (!selectedIds.length) { toast.error('Select at least one row.'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/admin/followups/pending/dispatch',
        { followup_ids: selectedIds });
      toast.success(`Dispatched ${data.dispatched} row(s). `
        + (data.rejected?.length ? `${data.rejected.length} rejected.` : ''));
      setSelected({});
      fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Dispatch failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="p-8 space-y-6 max-w-7xl"
          data-testid="admin-followups-pending-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-primary-800 border border-primary-200 bg-primary-50 rounded-full px-2 py-0.5">
            <ShieldCheck className="h-3 w-3" /> Draft-first safety
          </div>
          <h1 className="font-display text-3xl font-bold text-primary-900 mt-2">
            Follow-ups review
          </h1>
          <p className="text-neutral-600 text-sm max-w-2xl">
            Every follow-up email waits here as a draft. Nothing goes out
            until you approve it. After the recent send incident this is
            the only path an automated user-facing email can take.
          </p>
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setSelected({}); }}>
          <SelectTrigger className="w-44" data-testid="followups-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="draft">Drafts</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="dispatched">Dispatched</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <Button
          onClick={approve}
          disabled={busy || !selectedIds.length || status !== 'draft'}
          className="bg-primary-900 text-white hover:bg-primary-800"
          data-testid="followups-approve-btn"
        >
          <Check className="h-4 w-4 mr-2" />
          Approve ({selectedIds.length})
        </Button>
        <Button
          onClick={dispatchNow}
          disabled={busy || !selectedIds.length || status !== 'approved'}
          variant="outline"
          className="border-success-300 text-success-700 hover:bg-success-50"
          data-testid="followups-dispatch-btn"
        >
          <Send className="h-4 w-4 mr-2" />
          Dispatch now ({selectedIds.length})
        </Button>
        <div className="text-xs text-neutral-500 ml-2">
          Approve first (drafts) &rarr; then Dispatch (approved).
        </div>
      </div>

      {scheduler ? (
        <Card data-testid="followups-scheduler-tile">
          <CardContent className="p-3 grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
            <div>
              <div className="uppercase tracking-widest text-neutral-500">
                Scheduler
              </div>
              <div className="font-semibold text-primary-900 mt-1">
                {scheduler.scheduler?.started_at ? 'Running' : 'Not started'}
                {scheduler.scheduler?.dry_run ? ' (DRY-RUN)' : ''}
              </div>
              <div className="text-[10px] text-neutral-500">
                every {scheduler.scheduler?.interval_minutes} min
              </div>
            </div>
            <div>
              <div className="uppercase tracking-widest text-neutral-500">
                Next dispatch
              </div>
              <div className="font-semibold text-primary-900 mt-1"
                    data-testid="scheduler-next-dispatch">
                {scheduler.next_scheduled_dispatch_at
                  ? new Date(
                    scheduler.next_scheduled_dispatch_at,
                  ).toLocaleString()
                  : 'no approved due rows'}
              </div>
            </div>
            <div>
              <div className="uppercase tracking-widest text-neutral-500">
                Last run
              </div>
              <div className="font-semibold text-primary-900 mt-1"
                    data-testid="scheduler-last-run">
                {scheduler.scheduler?.last_run_at
                  ? new Date(scheduler.scheduler.last_run_at)
                    .toLocaleString()
                  : 'not yet'}
              </div>
            </div>
            <div>
              <div className="uppercase tracking-widest text-neutral-500">
                Last dispatched
              </div>
              <div className="font-semibold text-primary-900 mt-1">
                {scheduler.scheduler?.last_dispatched_count || 0}
              </div>
            </div>
            <div>
              <div className="uppercase tracking-widest text-neutral-500">
                Last failed
              </div>
              <div className={
                (scheduler.scheduler?.last_failed_count || 0) > 0
                  ? 'font-semibold text-red-800 mt-1'
                  : 'font-semibold text-primary-900 mt-1'
              } data-testid="scheduler-last-failed">
                {scheduler.scheduler?.last_failed_count || 0}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="followups-table">
            <thead className="bg-neutral-50 text-left text-xs uppercase tracking-widest text-neutral-500">
              <tr>
                <th className="p-3 w-8">
                  <Checkbox
                    checked={selectedIds.length > 0 && selectedIds.length === rows.length}
                    onCheckedChange={toggleAll}
                    aria-label="Select all rows"
                    data-testid="followups-select-all"
                  />
                </th>
                <th className="p-3">Recipient</th>
                <th className="p-3">Template</th>
                <th className="p-3">Source</th>
                <th className="p-3">Scheduled</th>
                <th className="p-3">Status</th>
                <th className="p-3">Approved</th>
                <th className="p-3">Dispatched</th>
                <th className="p-3">Response</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="p-6 text-center text-neutral-500">Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="p-6 text-center text-neutral-500"
                              data-testid="followups-empty">
                  No follow-ups in this bucket.
                </td></tr>
              ) : rows.map((r) => (
                <FollowupRow key={r.id} r={r}
                                selected={selected}
                                setSelected={setSelected} />
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

// -------------------------------------------------------------------- //
// Bundle 124 - row-expansion for /admin/followups/pending with a
// "Preview email HTML" collapsible per row. Renders the template
// body sample inside a sandboxed <iframe srcdoc> so any HTML in
// the template cannot script the admin page.
// -------------------------------------------------------------------- //
const FollowupRow = ({ r, selected, setSelected }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const bodyStub = (
    r.rendered_body_html
    || `<p>Hi,</p><p>Preview for template <strong>${r.template_variant}</strong>. `
    + `This is a static preview; the live email will be personalised at dispatch time.</p>`
    + `<p><a href="https://example.com/followups/preview">Share your update</a></p>`
    + `<p>If you would rather not hear about follow-ups again, `
    + `<a href="https://example.com/followups/opt-out/preview">opt out here</a>.</p>`
    + `<p>Warmly,<br>The Dostination Team</p>`
  );
  return (
    <>
      <tr className="border-t border-neutral-100 hover:bg-neutral-50"
            data-testid={`followup-row-${r.id}`}>
        <td className="p-3">
          <Checkbox
            checked={!!selected[r.id]}
            onCheckedChange={(v) => setSelected((s) => ({ ...s, [r.id]: !!v }))}
            aria-label={`Select ${r.email_masked}`}
            data-testid={`followup-select-${r.id}`}
          />
        </td>
        <td className="p-3 font-mono text-xs text-neutral-700">{r.email_masked}</td>
        <td className="p-3 text-xs text-neutral-700">
          {r.template_variant}
          <button type="button"
                    onClick={() => setPreviewOpen((v) => !v)}
                    className="ml-2 text-[10px] text-primary-700 underline"
                    data-testid={`followup-preview-toggle-${r.id}`}>
            {previewOpen ? 'Hide preview' : 'Preview'}
          </button>
        </td>
        <td className="p-3 text-xs text-neutral-600">{r.source}</td>
        <td className="p-3 text-xs text-neutral-600">
          {r.scheduled_send_at ? new Date(r.scheduled_send_at).toLocaleString() : '-'}
        </td>
        <td className="p-3">
          <Badge className={`${STATUS_BADGE[r.delivery_status] || STATUS_BADGE.draft} border`}
                  data-testid={`followup-status-${r.id}`}>
            {r.delivery_status}
          </Badge>
        </td>
        <td className="p-3 text-xs text-neutral-600">
          {r.approved_at ? new Date(r.approved_at).toLocaleString() : '-'}
        </td>
        <td className="p-3 text-xs text-neutral-600">
          {r.dispatched_at ? new Date(r.dispatched_at).toLocaleString() : '-'}
        </td>
        <td className="p-3 text-xs text-neutral-600">
          {r.response_at ? new Date(r.response_at).toLocaleString() : '-'}
        </td>
      </tr>
      {previewOpen ? (
        <tr data-testid={`followup-preview-row-${r.id}`}>
          <td colSpan={9} className="p-0 bg-neutral-50 border-t border-neutral-100">
            <div className="p-3">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                Email HTML preview (sandboxed)
              </div>
              <iframe
                title={`preview-${r.id}`}
                data-testid={`followup-preview-iframe-${r.id}`}
                sandbox=""
                srcDoc={bodyStub}
                style={{ width: '100%', minHeight: 220,
                            border: '1px solid #e5e5e5',
                            background: 'white' }}
              />
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
};

export default AdminFollowupsPendingPage;
